import { sql } from "drizzle-orm";
import { serializeWorkflowValue } from "@gencow/core";
import type { WorkflowStatus } from "@gencow/core";

export type DbLike = {
  execute: (query: ReturnType<typeof sql>) => Promise<unknown>;
};

export type WorkflowRow = {
  id: string;
  name: string;
  args: unknown;
  status: WorkflowStatus;
  current_step: string | null;
  result: unknown;
  error: string | null;
  error_code: string | null;
  retry_count: number;
  max_retries: number;
  max_duration_ms: number;
  first_claimed_at: string | Date | null;
  handler_started_at: string | Date | null;
  last_resume_attempt_at: string | Date | null;
  runnable_at: string | Date | null;
  started_at: string | Date;
  updated_at: string | Date;
  completed_at: string | Date | null;
  user_id: string | null;
};

export type WorkflowStepRow = {
  workflow_id: string;
  step_name: string;
  status: WorkflowStatus;
  output: unknown;
  error: string | null;
  started_at: string | Date | null;
  updated_at: string | Date;
  completed_at: string | Date | null;
};

export type WorkflowSleepState = {
  wakeAt: string;
  durationMs: number;
};

export type WorkflowWaitState = {
  eventName: string;
  timeoutAt: string | null;
  timeoutMs: number | null;
};

type WorkflowEventRow = {
  id: string;
  payload: unknown;
};

function rowsFromResult<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown[] }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

export async function getWorkflowRow(db: DbLike, workflowId: string): Promise<WorkflowRow | null> {
  const result = await db.execute(sql`
        SELECT
            id,
            name,
            args,
            status,
            current_step,
            result,
            error,
            error_code,
            retry_count,
            max_retries,
            max_duration_ms,
            first_claimed_at,
            handler_started_at,
            last_resume_attempt_at,
            runnable_at,
            started_at,
            updated_at,
            completed_at,
            user_id
        FROM _gencow_workflows
        WHERE id = ${workflowId}
        LIMIT 1
    `);
  return rowsFromResult<WorkflowRow>(result)[0] ?? null;
}

export async function claimWorkflowRun(db: DbLike, workflowId: string): Promise<WorkflowRow | null> {
  const result = await db.execute(sql`
        UPDATE _gencow_workflows
        SET
            status = 'running',
            error = NULL,
            error_code = NULL,
            runnable_at = NULL,
            first_claimed_at = COALESCE(first_claimed_at, NOW()),
            last_resume_attempt_at = NOW(),
            updated_at = NOW()
        WHERE id = ${workflowId}
          AND (
              (
                  status = 'pending'
                  AND (runnable_at IS NULL OR runnable_at <= NOW())
              )
              OR (
                  status = 'running'
                  AND updated_at < NOW() - INTERVAL '15 minutes'
              )
          )
        RETURNING
            id,
            name,
            args,
            status,
            current_step,
            result,
            error,
            error_code,
            retry_count,
            max_retries,
            max_duration_ms,
            first_claimed_at,
            handler_started_at,
            last_resume_attempt_at,
            runnable_at,
            started_at,
            updated_at,
            completed_at,
            user_id
    `);
  return rowsFromResult<WorkflowRow>(result)[0] ?? null;
}

export async function getWorkflowStep(
  db: DbLike,
  workflowId: string,
  stepName: string,
): Promise<WorkflowStepRow | null> {
  const result = await db.execute(sql`
        SELECT
            workflow_id,
            step_name,
            status,
            output,
            error,
            started_at,
            updated_at,
            completed_at
        FROM _gencow_workflow_steps
        WHERE workflow_id = ${workflowId}
          AND step_name = ${stepName}
        LIMIT 1
    `);
  return rowsFromResult<WorkflowStepRow>(result)[0] ?? null;
}

export async function claimWorkflowEvent(
  db: DbLike,
  workflowId: string,
  eventName: string,
): Promise<{ id: string; payload: unknown } | null> {
  const selectResult = await db.execute(sql`
        SELECT id, payload
        FROM _gencow_workflow_events
        WHERE workflow_id = ${workflowId}
          AND event_name = ${eventName}
          AND consumed_at IS NULL
        ORDER BY received_at ASC, id ASC
        LIMIT 1
    `);
  const row = rowsFromResult<WorkflowEventRow>(selectResult)[0] ?? null;
  if (!row) return null;

  await db.execute(sql`
        UPDATE _gencow_workflow_events
        SET consumed_at = NOW()
        WHERE id = ${row.id}
    `);
  return row;
}

export async function markWorkflowFailed(
  db: DbLike,
  workflowId: string,
  error: string,
  retryCount: number,
  errorCode: string | null = null,
): Promise<void> {
  await db.execute(sql`
        UPDATE _gencow_workflows
        SET
            status = 'failed',
            error = ${error},
            error_code = ${errorCode},
            runnable_at = NULL,
            retry_count = ${retryCount},
            completed_at = NOW(),
            updated_at = NOW()
        WHERE id = ${workflowId}
    `);
}

export async function markWorkflowCompleted(db: DbLike, workflowId: string, result: unknown): Promise<void> {
  const persisted = serializeWorkflowValue(result);
  await db.execute(sql`
        UPDATE _gencow_workflows
        SET
            status = 'completed',
            result = ${JSON.stringify(persisted)}::jsonb,
            error = NULL,
            error_code = NULL,
            runnable_at = NULL,
            current_step = NULL,
            completed_at = NOW(),
            updated_at = NOW()
        WHERE id = ${workflowId}
    `);
}

export async function markWorkflowPendingAtStep(
  db: DbLike,
  workflowId: string,
  stepName: string,
): Promise<void> {
  await db.execute(sql`
        UPDATE _gencow_workflows
        SET
            status = 'pending',
            current_step = ${stepName},
            error = NULL,
            error_code = NULL,
            runnable_at = NULL,
            updated_at = NOW(),
            completed_at = NULL
        WHERE id = ${workflowId}
    `);
}

export async function requeueWorkflow(
  db: DbLike,
  workflowId: string,
  retryCount: number,
  error: string,
  errorCode: string | null = null,
  retryAfterMs: number | null = null,
): Promise<void> {
  const runnableAt = retryAfterMs == null ? sql`NULL` : sql`NOW() + (${retryAfterMs} * INTERVAL '1 millisecond')`;
  await db.execute(sql`
        UPDATE _gencow_workflows
        SET
            status = 'pending',
            retry_count = ${retryCount},
            error = ${error},
            error_code = ${errorCode},
            runnable_at = ${runnableAt},
            updated_at = NOW()
        WHERE id = ${workflowId}
    `);
}

export async function markWorkflowHandlerStarted(
  db: DbLike,
  workflowId: string,
  startedAt: string | Date,
): Promise<void> {
  await db.execute(sql`
        UPDATE _gencow_workflows
        SET
            handler_started_at = COALESCE(handler_started_at, ${startedAt}::timestamptz),
            updated_at = NOW()
        WHERE id = ${workflowId}
    `);
}

export async function requeueWorkflowBeforeFirstStep(
  db: DbLike,
  workflowId: string,
  retryCount: number,
  error: string,
  errorCode: string,
  retryAfterMs: number | null = null,
): Promise<void> {
  const runnableAt = retryAfterMs == null ? sql`NULL` : sql`NOW() + (${retryAfterMs} * INTERVAL '1 millisecond')`;
  await db.execute(sql`
        UPDATE _gencow_workflows
        SET
            status = 'pending',
            retry_count = ${retryCount},
            current_step = NULL,
            handler_started_at = NULL,
            error = ${error},
            error_code = ${errorCode},
            runnable_at = ${runnableAt},
            updated_at = NOW()
        WHERE id = ${workflowId}
    `);
}

export async function markWorkflowSleepStepPending(
  db: DbLike,
  workflowId: string,
  stepName: string,
  sleepState: WorkflowSleepState,
): Promise<void> {
  const persisted = serializeWorkflowValue(sleepState);
  await db.execute(sql`
        INSERT INTO _gencow_workflow_steps (
            workflow_id,
            step_name,
            status,
            output,
            error,
            started_at,
            updated_at,
            completed_at
        )
        VALUES (
            ${workflowId},
            ${stepName},
            'pending',
            ${JSON.stringify(persisted)}::jsonb,
            NULL,
            NOW(),
            NOW(),
            NULL
        )
        ON CONFLICT (workflow_id, step_name)
        DO UPDATE SET
            status = 'pending',
            output = ${JSON.stringify(persisted)}::jsonb,
            error = NULL,
            started_at = COALESCE(_gencow_workflow_steps.started_at, NOW()),
            updated_at = NOW(),
            completed_at = NULL
    `);
}

export async function markWorkflowSleepStepCompleted(
  db: DbLike,
  workflowId: string,
  stepName: string,
  sleepState: WorkflowSleepState,
): Promise<void> {
  const persisted = serializeWorkflowValue(sleepState);
  await db.execute(sql`
        INSERT INTO _gencow_workflow_steps (
            workflow_id,
            step_name,
            status,
            output,
            error,
            started_at,
            updated_at,
            completed_at
        )
        VALUES (
            ${workflowId},
            ${stepName},
            'completed',
            ${JSON.stringify(persisted)}::jsonb,
            NULL,
            NOW(),
            NOW(),
            NOW()
        )
        ON CONFLICT (workflow_id, step_name)
        DO UPDATE SET
            status = 'completed',
            output = ${JSON.stringify(persisted)}::jsonb,
            error = NULL,
            started_at = COALESCE(_gencow_workflow_steps.started_at, NOW()),
            updated_at = NOW(),
            completed_at = NOW()
    `);
}

export async function markWorkflowWaitStepPending(
  db: DbLike,
  workflowId: string,
  stepName: string,
  waitState: WorkflowWaitState,
): Promise<void> {
  const persisted = serializeWorkflowValue(waitState);
  await db.execute(sql`
        INSERT INTO _gencow_workflow_steps (
            workflow_id,
            step_name,
            status,
            output,
            error,
            started_at,
            updated_at,
            completed_at
        )
        VALUES (
            ${workflowId},
            ${stepName},
            'pending',
            ${JSON.stringify(persisted)}::jsonb,
            NULL,
            NOW(),
            NOW(),
            NULL
        )
        ON CONFLICT (workflow_id, step_name)
        DO UPDATE SET
            status = 'pending',
            output = ${JSON.stringify(persisted)}::jsonb,
            error = NULL,
            started_at = COALESCE(_gencow_workflow_steps.started_at, NOW()),
            updated_at = NOW(),
            completed_at = NULL
    `);
}

export async function markWorkflowWaitStepCompleted(
  db: DbLike,
  workflowId: string,
  stepName: string,
  payload: unknown,
): Promise<void> {
  const persisted = serializeWorkflowValue(payload);
  await db.execute(sql`
        INSERT INTO _gencow_workflow_steps (
            workflow_id,
            step_name,
            status,
            output,
            error,
            started_at,
            updated_at,
            completed_at
        )
        VALUES (
            ${workflowId},
            ${stepName},
            'completed',
            ${JSON.stringify(persisted)}::jsonb,
            NULL,
            NOW(),
            NOW(),
            NOW()
        )
        ON CONFLICT (workflow_id, step_name)
        DO UPDATE SET
            status = 'completed',
            output = ${JSON.stringify(persisted)}::jsonb,
            error = NULL,
            started_at = COALESCE(_gencow_workflow_steps.started_at, NOW()),
            updated_at = NOW(),
            completed_at = NOW()
    `);
}

export async function markWorkflowWaitStepFailed(
  db: DbLike,
  workflowId: string,
  stepName: string,
  error: string,
): Promise<void> {
  await db.execute(sql`
        INSERT INTO _gencow_workflow_steps (
            workflow_id,
            step_name,
            status,
            output,
            error,
            started_at,
            updated_at,
            completed_at
        )
        VALUES (
            ${workflowId},
            ${stepName},
            'failed',
            NULL,
            ${error},
            NOW(),
            NOW(),
            NULL
        )
        ON CONFLICT (workflow_id, step_name)
        DO UPDATE SET
            status = 'failed',
            output = _gencow_workflow_steps.output,
            error = ${error},
            started_at = COALESCE(_gencow_workflow_steps.started_at, NOW()),
            updated_at = NOW(),
            completed_at = NULL
    `);
}

export async function listRecoverableWorkflows(
  db: DbLike,
): Promise<Array<{ id: string; name: string; retryDelayMs: number }>> {
  const result = await db.execute(sql`
        SELECT
            id,
            name,
            CASE
                WHEN status = 'pending'
                 AND runnable_at IS NOT NULL
                 AND runnable_at > NOW()
                THEN CEIL(EXTRACT(EPOCH FROM (runnable_at - NOW())) * 1000)::int
                ELSE 0
            END AS retry_delay_ms
        FROM _gencow_workflows
        WHERE status = 'pending'
           OR (
               status = 'running'
               AND updated_at < NOW() - INTERVAL '15 minutes'
           )
        ORDER BY started_at ASC
        LIMIT 100
    `);
  return rowsFromResult<{ id: string; name: string; retry_delay_ms?: number | string | null }>(result).map(
    (row) => ({
      id: row.id,
      name: row.name,
      retryDelayMs: Math.max(0, Number(row.retry_delay_ms) || 0),
    }),
  );
}
